import type { StringKey } from "./fa";

/**
 * Pashto (ps). Typed against `StringKey`, so it must define every key.
 *
 * Phrasings are kept nominal or imperative, and past states are written as
 * intransitive/passive ("پټنوم بدل شو"), to stay clear of the split-ergative
 * trap where a past-tense transitive verb must agree with its object. This
 * set still needs a native Pashto speaker's review before it is trusted —
 * see ROADMAP debts and PRIORITIES.
 */
export const ps: Record<StringKey, string> = {
  // common
  common_menu: "مینو",
  common_close: "بندول",
  common_cancel: "لغوه",
  common_language: "ژبه",

  // app shell
  app_name: "تالار",
  app_initial: "ت",

  // nav
  nav_group_ops: "عملیات",
  nav_group_finance: "مالي او اداري",
  nav_group_market: "بازار",
  nav_group_platform: "پلټفارم",
  nav_dashboard: "ډشبورډ",
  nav_calendar: "د بکنګ کلیزه",
  nav_bookings: "بکنګونه",
  nav_contracts: "تړونونه",
  nav_crm: "پیرودونکي",
  nav_finance: "مالي",
  nav_payouts: "تسویې",
  nav_hr: "کارکوونکي",
  nav_halls: "تالارونه",
  nav_reviews: "نظرونه",
  nav_admin: "د پلټفارم مدیریت",
  nav_change_password: "د پټنوم بدلون",
  nav_logout: "وتل",
  layout_panel_fallback: "د مدیریت پینل",
  layout_today: "نن: {date}",

  // login
  login_subtitle: "د واده تالارونو د مدیریت پینل",
  login_email: "ایمیل",
  login_password: "پټنوم",
  login_submit: "ننوتل",
  login_submitting: "د ننوتلو په حال کې…",
  login_failed: "ننوتنه ناکامه شوه",

  // onboarding
  onboarding_welcome: "ښه راغلاست",
  onboarding_intro:
    "د پیل لپاره، د خپل کاروبار یا تالار نوم ثبت کړئ. له دې پړاو وروسته، بشپړ پینل (بکنګونه، کلیزه، مالي، کارکوونکي، پیرودونکي…) ستاسو لپاره پرانیستل کیږي.",
  onboarding_name_label: "د سازمان / تالار نوم",
  onboarding_name_placeholder: "لکه: د آریانا تالارونو شرکت",
  onboarding_submit: "سازمان جوړول او پینل ته ننوتل",
  onboarding_submitting: "د جوړولو په حال کې…",
  onboarding_error: "د سازمان په جوړولو کې ستونزه",

  // route guard
  guard_no_access_title: "لاسرسی نلرئ",
  guard_no_access_body:
    "دا برخه د «{roles}» رول لپاره ده. د لاسرسي لپاره د تالار له مالک سره اړیکه ونیسئ.",

  // dashboard
  dashboard_title: "ډشبورډ",
  dashboard_kpi_confirmed: "تایید شوي بکنګونه",
  dashboard_kpi_pending_requests: "په تمه غوښتنې",
  dashboard_kpi_pending_requests_hint: "ستاسو تایید ته اړتیا لري",
  dashboard_kpi_pending_deposits: "د بیعانې په تمه",
  dashboard_kpi_upcoming: "د راتلونکو ۷ ورځو محفلونه",
  dashboard_insights_title: "د تالار هوښیارتیا",

  // change password
  cp_title: "د پټنوم بدلول",
  cp_intro: "د خوندیتوب لپاره، لومړی اوسنی پټنوم ولیکئ.",
  cp_current_label: "اوسنی پټنوم",
  cp_new_label: "نوی پټنوم",
  cp_repeat_label: "د نوي پټنوم تکرار",
  cp_min_helper: "لږ تر لږه {n} توري",
  cp_submit: "پټنوم بدلول",
  cp_submitting: "د بدلولو په حال کې…",
  cp_success: "پټنوم بدل شو. بل ځل په نوي پټنوم ننوځئ.",
  cp_min_length: "نوی پټنوم باید لږ تر لږه {n} توري ولري.",
  cp_mismatch: "نوی پټنوم او د هغه تکرار سره برابر نه دي.",
  cp_same_as_current: "نوی پټنوم له اوسني پټنوم سره توپیر نلري.",
  cp_err_no_email: "دا حساب په ایمیل سره نه دی ننوتی — پټنوم له دې ځایه نشي بدلیدی.",
  cp_err_wrong_password: "اوسنی پټنوم سم نه دی.",
  cp_err_weak: "نوی پټنوم ډېر ساده دی — توري او عددونه سره ګډ کړئ.",
  cp_err_too_many: "ډېرې هڅې وشوې — څو دقیقې صبر وکړئ او بیا هڅه وکړئ.",
  cp_err_recent_login: "ستاسو ناسته زړه شوه — یو ځل ووځئ او بیا ننوځئ.",
  cp_err_network: "اړیکه ونه نیول شوه — انټرنیټ وګورئ.",
  cp_err_generic: "د پټنوم بدلون ناکام شو — بیا هڅه وکړئ.",

  // shared — actions, columns, enums
  common_error: "تېروتنه",
  common_send: "لیږل",
  common_empty: "هیڅ شتون نلري",
  common_status: "حالت",
  shift_morning: "سهار",
  shift_evening: "ماښام",
  shift_full_day: "ټوله ورځ",
  booking_status_pending_request: "د تایید په تمه",
  booking_status_pending_deposit: "د بیعانې په تمه",
  booking_status_confirmed: "تایید شوی",
  booking_status_completed: "ترسره شوی",
  booking_status_cancelled: "لغوه شوی",

  // bookings
  bookings_title: "بکنګونه",
  booking_tab_requests: "غوښتنې",
  booking_tab_pending_deposit: "د بیعانې په تمه",
  booking_tab_confirmed: "تایید شوي",
  booking_tab_completed: "ترسره شوي",
  bookings_col_date: "نیټه",
  bookings_col_shift: "وخت",
  bookings_col_guests: "میلمانه",
  bookings_col_total: "ټول",
  bookings_col_paid: "تادیه شوی",
  bookings_col_balance: "پاتې",
  bookings_confirm: "تایید",
  bookings_contract: "تړون",
  bookings_issue_contract: "د تړون صادرول",
  bookings_toast_confirmed: "غوښتنه تایید شوه — پیرودونکي ته د بیعانې د تادیې خبر ورکړل شو",
  bookings_toast_contract_issued: "تړون صادر شو — پیرودونکي ته د لاسلیک خبر ورکړل شو",
  bookings_toast_receipt: "رسید {no} ثبت شو — په مالي پاڼه کې یې تایید کړئ ترڅو دفترونو ته ولاړ شي",

  // receipt dialog
  receipt_title: "د رسید ثبتول",
  receipt_submit: "رسید ثبتول",
  receipt_balance: "د دې بکنګ پاتې: {amount}",
  receipt_amount_label: "اندازه (افغانۍ)",
  receipt_method_label: "د ترلاسه کولو طریقه",
  receipt_method_cash: "نغد (صندوق)",
  receipt_method_bank: "بانکي حواله",
  receipt_note_label: "یادښت (اختیاري)",
  receipt_failed: "رسید ثبت نشو",

  // calendar
  calendar_day_tooltip: "{count} میلمانه · {status}",

  // chats
  chats_title: "له پیرودونکو سره خبرې",
  chats_empty: "تر اوسه خبرې نشته",
  chats_reply_placeholder: "ځواب ولیکئ…",

  // reviews
  reviews_title: "د پیرودونکو نظرونه",
  reviews_your_reply: "ستاسو ځواب",
  reviews_reply_placeholder: "خپل ځواب ولیکئ…",
  reviews_empty: "تر اوسه هیڅ خپور شوی نظر نشته",

  // contracts (page)
  contracts_title: "تړونونه",
  contracts_subtitle:
    "مهر شوي ډیجیټل تړونونه — د بکنګ له پاڼې صادریږي، د پیرودونکي له خوا یا په تالار کې حضوري لاسلیک کیږي",
  contracts_all: "ټول",
  contracts_awaiting_signature: "د لاسلیک په تمه",
  contracts_signed: "لاسلیک شوی",
  contracts_status_signed: "لاسلیک شوی ✓",
  contracts_stat_total: "د تړونونو ټوله",
  contracts_stat_last_n: "وروستي {n}",
  contracts_hint_unsigned: "پیرودونکي لا لاسلیک نه دی کړی",
  contracts_col_issued: "د صدور نیټه",
  contracts_col_signed_date: "د لاسلیک نیټه",
  contracts_col_template: "کینډۍ",
  contracts_col_seal: "ډیجیټل مهر",
  contracts_template_version: "نسخه {v}",
  contracts_view_print: "کتل او چاپ",
  contracts_empty_none:
    "تر اوسه هیڅ تړون نه دی صادر شوی — د بکنګ له پاڼې یې د یو تایید شوي بکنګ لپاره صادر کړئ",
  contracts_empty_filtered: "په دې حالت کې څه نشته",

  // contract dialog
  contract_dialog_title: "ډیجیټل تړون",
  contract_awaiting_customer: "د پیرودونکي د لاسلیک په تمه",
  contract_seal_line: "ډیجیټل مهر: {hash}",
  contract_inperson_note:
    "که پیرودونکي تړون په تالار کې حضوري لاسلیک کړی، دلته یې ثبت کړئ ترڅو نهایي شي.",
  contract_print_pdf: "چاپ / د PDF ساتل",
  contract_signing: "د ثبتولو په حال کې…",
  contract_sign_inperson: "د پیرودونکي حضوري لاسلیک ثبتول",
  contract_sign_failed: "لاسلیک ثبت نشو",

  // finance
  finance_title: "مالي — عاید او لګښتونه",
  finance_net_profit: "خالص ګټه",
  finance_total_income: "ټول عاید",
  finance_total_expenses: "ټول لګښتونه",
  finance_pending_receipts: "د تایید په تمه رسیدونه",
  finance_col_receipt: "رسید",
  finance_col_method: "طریقه",
  finance_col_amount: "اندازه",
  finance_col_note: "یادښت",
  finance_method_cash: "نغد",
  finance_method_bank: "بانک",
  finance_verify_post: "تایید او دفترونو ته ثبت",
  finance_payouts_link: "د پلټفارم تسویې (آنلاین تادیات) →",
  finance_coa_title: "حسابونه (Chart of Accounts)",
  finance_col_code: "کوډ",
  finance_col_account: "حساب",
  finance_col_type: "ډول",
  finance_col_balance: "بیلانس",

  // payouts
  payouts_title: "د پلټفارم تسویې",
  payouts_subtitle:
    "د پیرودونکو آنلاین تادیات لومړی د پلټفارم حساب ته ځي؛ پلټفارم د کمیشن له کمولو وروسته خالص یې د تالار بانکي حساب ته لیږدوي",
  payouts_refresh: "بیا بارول",
  payouts_stat_pending: "تاسو ته د تادیې په تمه",
  payouts_stat_paid: "تر اوسه تادیه شوی",
  payouts_stat_commission: "د پلټفارم کمیشن",
  payouts_stat_commission_hint: "د ټولو دورو له مجموعې",
  payouts_col_period: "د تسویې دوره تر",
  payouts_col_count: "د تادیاتو شمیر",
  payouts_col_gross: "ناخالص",
  payouts_col_commission: "کمیشن",
  payouts_col_net: "خالص ورکړه",
  payouts_col_paid_date: "د تادیې نیټه",
  payouts_status_paid: "تادیه شوی",
  payouts_status_pending: "په تمه",
  payouts_commission_cell: "{amount} ({pct}٪)",
  payouts_empty:
    "تر اوسه هیڅ تسویه نه ده شوې — تسویه هغه وخت جوړیږي چې یو تایید شوی، نا-تسویه شوی آنلاین تادیه موجوده وي",
  payouts_loading: "د بارولو په حال کې…",
  payouts_footnote:
    "هغه بیعانې او نغدي تادیات چې تاسو یې په تالار کې اخلئ په دې جدول کې نشته — هغه پیسې له پیله ستاسو سره دي او د «مالي» پاڼه کې ثبتیږي.",
  payouts_load_failed: "د تسویو راوړل ناکام شول",

  // shared (more)
  common_submit: "ثبتول",
  common_note_optional: "یادښت (اختیاري)",

  // crm
  crm_title: "احتمالي پیرودونکي",
  crm_new_lead: "نوی احتمالي پیرودونکی",
  crm_stage_new: "نوی",
  crm_stage_contacted: "اړیکه نیول شوې",
  crm_stage_visited: "لیدنه شوې",
  crm_stage_quoted: "بیه ورکړل شوه",
  crm_stage_won: "بریالی",
  crm_stage_lost: "ناکام",
  crm_source_walk_in: "حضوري",
  crm_source_phone: "تلیفوني",
  crm_source_instagram: "انستاګرام",
  crm_source_referral: "معرفي",
  crm_source_other: "نور",
  crm_guest_estimate_chip: "~{count} میلمانه",
  crm_event_approx: "نږدې {date}",
  crm_advance_next: "{label} ←",
  crm_toast_lead_saved: "ثبت شو",
  crm_dialog_title: "نوی احتمالي پیرودونکی",
  crm_name: "نوم",
  crm_phone: "د اړیکې شمیره",
  crm_source_label: "سرچینه",
  crm_guests_label: "د میلمنو اټکل (اختیاري)",

  // hr
  hr_title: "کارکوونکي او معاشونه",
  hr_new_employee: "نوی کارکوونکی",
  hr_create_payroll: "د {period} د معاش لیست جوړول",
  hr_col_name: "نوم",
  hr_col_role: "دنده",
  hr_col_salary: "میاشتنی معاش",
  hr_col_today_attendance: "د نن حاضري",
  hr_check_inout: "د ننوتل/وتل ثبت",
  hr_empty_employees: "تر اوسه هیڅ کارکوونکی نه دی ثبت شوی",
  hr_payroll_runs_title: "د معاش لیستونه",
  hr_col_period: "دوره (لمریز)",
  hr_col_employees: "کارکوونکي",
  hr_col_net_total: "خالص مجموعه",
  hr_run_status_posted: "تادیه شوی",
  hr_run_status_draft: "مسوده",
  hr_post_run: "تادیه او دفترونو ته ثبت",
  hr_empty_runs: "هیڅ د معاش لیست نه دی جوړ شوی",
  hr_dialog_title: "نوی کارکوونکی",
  hr_full_name: "بشپړ نوم",
  hr_salary_label: "میاشتنی معاش (افغانۍ)",
  hr_toast_employee_saved: "کارکوونکی ثبت شو",
  hr_toast_checked_in: "د ننوتلو حاضري ثبت شوه",
  hr_toast_checked_out: "وتل ثبت شو",
  hr_toast_payroll_created: "د {period} معاش لیست جوړ شو — {count} کارکوونکي، مجموع {total}",
  hr_toast_payroll_posted: "معاشونه تادیه او دفترونو ته ثبت شول (د معاشاتو لګښت / صندوق)",

  // halls (page)
  halls_title: "زما تالارونه",
  halls_add: "تالار ثبتول",
  hall_status_draft: "مسوده",
  hall_status_pending_review: "د بیاکتنې په تمه",
  hall_status_published: "خپور شوی",
  hall_status_suspended: "ځنډول شوی",
  halls_capacity: "ظرفیت {total} کسه",
  halls_price_from: "· فی کس له {price}",
  halls_rating: "★ {avg} ({count} نظرونه)",
  hall_edit: "سمون",
  hall_uploading: "د پورته کولو په حال کې…",
  hall_add_photo: "عکس",
  hall_photos: "عکسونه",
  hall_add_menu: "مینو",
  hall_submit_publish: "د خپرولو لپاره لیږل",
  hall_price_suggestion: "د بیې وړاندیز",
  halls_empty: "تر اوسه مو هیڅ تالار نه دی ثبت کړی — د «تالار ثبتول» تڼۍ سره پیل وکړئ.",
  halls_toast_photo_too_big: "عکس باید له ۱۵ میګابایټ کم وي",
  halls_toast_photo_added: "عکس اضافه شو",
  halls_toast_photo_failed: "د عکس پورته کول ناکام شول",
  halls_toast_sent_publish: "د بیاکتنې او خپرولو لپاره ولیږل شو",
  halls_toast_hall_created:
    "تالار ثبت شو — اوس یې مینوګانې اضافه کړئ، بیا «د خپرولو لپاره لیږل» کېکاږئ",
  halls_toast_hall_saved: "د تالار بدلونونه خوندي شول",
  halls_toast_menu_created: "مینو ثبت شو",
  halls_price_dialog_title: "د بیې وړاندیز — {hall}",
  halls_price_occupancy: "د راتلونکو ۹۰ ورځو ډکوالی: {pct}٪",
  halls_price_peak: "· تر ټولو پرتقاضا ورځ: {day}",
  halls_price_col_menu: "مینو",
  halls_price_col_current: "اوسنۍ بیه (فی کس)",
  halls_price_col_suggested: "وړاندیز",
  halls_price_footnote: "وړاندیزونه مشورتي دي — د بیې بدلون تل ستاسو په تایید دی.",

  // hall types
  hall_type_luxury: "لوکس",
  hall_type_standard: "معیاري",
  hall_type_garden: "باغ",
  hall_type_vip_salon: "VIP سالون",

  // amenities
  amenity_parking: "پارکینګ",
  amenity_generator: "جنراتور",
  amenity_bridal_room: "د ناوې خونه",
  amenity_vip_room: "VIP خونه",
  amenity_av_system: "غږیز سیستم",
  amenity_garden: "باغ",

  // hall form
  hallform_edit_title: "د تالار سمون",
  hallform_create_title: "نوی تالار ثبتول",
  hallform_name: "د تالار نوم",
  hallform_city: "ښار",
  hallform_type: "د تالار ډول",
  hallform_cap_total: "ټول ظرفیت",
  hallform_cap_mens: "نارینه",
  hallform_cap_womens: "ښځینه",
  hallform_hall_fee: "د تالار کرایه (افغانۍ، له فی کس جلا)",
  hallform_deposit_pct: "بیعانه (٪ له مجموعې)",
  hallform_instant: "سمدستي بکنګ (ستاسو تایید ته اړتیا نشته، مستقیم بیعانې پړاو ته ځي)",
  hallform_amenities: "اسانتیاوې",
  hallform_cancel_policy:
    "د لغوې اصلي شرایط: تر ۳۰ ورځو مخکې ۱۰۰٪، تر ۷ ورځو مخکې ۵۰٪ د بیعانې بیرته ورکړه — وروسته د سمون وړ دی.",
  hallform_save_changes: "بدلونونه خوندي کول",
  hallform_create_submit: "تالار ثبتول",
  hallform_error: "د تالار ساتل ناکام شول",

  // menu form
  menuform_title: "نوې مینو (فی کس بیه)",
  menuform_name: "د مینو نوم",
  menuform_name_placeholder: "لکه: عادي مینو",
  menuform_price: "فی کس بیه (افغانۍ)",
  menuform_courses: "خوراکونه (په ، سره جلا کړئ)",
  menuform_courses_placeholder: "قابلي، کباب، سلاته، څښاک",
  menuform_submit: "مینو ثبتول",
  menuform_error: "د مینو ثبتول ناکام شول",

  // shared (more)
  common_delete: "ړنګول",
  common_reject: "ردول",
  common_publish: "خپرول",

  // hall photos dialog
  photos_load_failed: "عکسونه بار نشول",
  photos_delete_failed: "د عکس ړنګول ناکام شول",
  photos_title: "د {hall} عکسونه",
  photos_intro:
    "لومړی عکس د تالار اصلي عکس دی او د لټون په پایلو کې ښکاري. د هغه په ړنګولو سره، راتلونکی عکس یې ځای نیسي.",
  photos_empty: "تر اوسه هیڅ عکس نه دی اضافه شوی — پرته له عکسه، تالار د لټون په پایلو کې تش ځای ښيي.",
  photos_cover_chip: "اصلي عکس",
  photos_delete_aria: "عکس ړنګول",
  photos_add: "عکس اضافه کول",
  photos_confirm_title: "دا عکس دې ړنګ شي؟",
  photos_confirm_cover: "دا د تالار اصلي عکس دی. د هغه په ړنګولو سره، راتلونکی عکس یې ځای نیسي.",
  photos_confirm_generic: "دا کار بیرته نه راګرځي.",

  // printable receipt
  receipt_print_title: "د تادیې رسید",
  receipt_print_subtitle: "د {purpose} رسید",
  receipt_print_no: "د رسید شمیره",
  receipt_print_issued: "د صدور نیټه",
  receipt_print_hall: "تالار",
  receipt_print_customer: "پیرودونکی",
  receipt_print_phone: "د اړیکې شمیره",
  receipt_print_event_date: "د محفل نیټه",
  receipt_print_method: "د تادیې طریقه",
  receipt_print_amount: "ترلاسه شوې اندازه",
  receipt_print_contract_total: "د تړون ټوله",
  receipt_print_total_paid: "ټوله تادیه شوې",
  receipt_print_balance: "پاتې",
  receipt_print_note: "یادښت",
  receipt_print_hall_sign: "د تالار لاسلیک: ____________",
  receipt_print_customer_sign: "د پیرودونکي لاسلیک: ____________",
  receipt_print_footer: "دا رسید د تالار له سیسټمه صادر شوی دی.",

  // admin
  admin_kpi_published: "خپاره شوي تالارونه",
  admin_kpi_awaiting: "د بیاکتنې په تمه",
  admin_kpi_confirmed: "تایید شوي بکنګونه",
  admin_kpi_tickets: "خلاص ټکټونه",
  admin_pending_halls_title: "د بیاکتنې په تمه تالارونه",
  admin_col_name: "نوم",
  admin_col_city: "ښار",
  admin_col_type: "ډول",
  admin_col_capacity: "ظرفیت",
  admin_approve_publish: "تایید او خپرول",
  admin_empty_pending: "د بیاکتنې په تمه څه نشته",
  admin_settlement_title: "د تادیاتو تسویه",
  admin_run_settlement: "د تسویې پلي کول",
  admin_running: "د پلي کولو په حال کې…",
  admin_col_org: "سازمان",
  admin_col_gross: "ناخالص",
  admin_col_commission: "کمیسیون",
  admin_col_net: "خالص",
  admin_mark_paid: "تادیه ثبتول",
  admin_empty_payouts: "د نا-تسویه شوو تادیاتو د لیدلو لپاره «د تسویې پلي کول» کېکاږئ",
  admin_reviews_title: "د تایید په تمه نظرونه",
  admin_col_hall: "تالار",
  admin_col_rating: "درجه",
  admin_col_text: "متن",
  admin_col_date: "نیټه",
  admin_empty_reviews: "د تایید په تمه نظر نشته",

  // sign-up / password reset / auth errors (LoginPage)
  login_no_account: "لا حساب نه لرئ؟",
  login_create_account: "د تالار لپاره حساب جوړول",
  login_have_account: "حساب لرئ؟ ننوځئ",
  login_forgot: "پټنوم مو هېر کړی؟",
  signup_title: "د خپل تالار لپاره حساب جوړول",
  signup_subtitle: "وړیا دی. له حساب جوړولو وروسته، د خپل تالار یا شرکت نوم ثبتوئ.",
  signup_name: "نوم او تخلص",
  signup_password_repeat: "د پټنوم تکرار",
  signup_submit: "حساب جوړول",
  signup_submitting: "د حساب د جوړولو په حال کې…",
  signup_password_short: "پټنوم باید لږ تر لږه {n} توري ولري",
  signup_password_mismatch: "پټنوم او د هغه تکرار سره برابر نه دي",
  forgot_need_email: "لومړی خپل ایمیل پورته ولیکئ",
  forgot_sent:
    "که دا ایمیل په تالار کې حساب ولري، د پټنوم بدلولو لینک ورته ولیږل شو. خپل ایمیل (او Spam) وګورئ.",
  auth_err_invalid_credential: "ایمیل یا پټنوم سم نه دی",
  auth_err_email_in_use: "په دې ایمیل مخکې حساب جوړ شوی — ننوځئ یا پټنوم بیرته ترلاسه کړئ",
  auth_err_invalid_email: "ایمیل سم نه دی لیکل شوی",
  auth_err_weak: "پټنوم ډېر ساده دی — توري او عددونه سره ګډ کړئ",
  auth_err_disabled: "دا حساب غیرفعال شوی — د تالار له ملاتړ سره اړیکه ونیسئ",
  auth_err_generic: "ننوتنه ناکامه شوه — بیا هڅه وکړئ",

  // calendar (Jalali month grid)
  calendar_today: "نن",
  calendar_prev_month: "تېره میاشت",
  calendar_next_month: "راتلونکې میاشت",
  calendar_legend_free: "تش",
  calendar_legend_partial: "یو وخت بک شوی",
  calendar_legend_full: "ډک",
  calendar_month_summary: "په دې میاشت کې {count} بکنګونه",
  weekday_sat: "شنبه",
  weekday_sun: "یکشنبه",
  weekday_mon: "دوشنبه",
  weekday_tue: "درېشنبه",
  weekday_wed: "څلورشنبه",
  weekday_thu: "پنځشنبه",
  weekday_fri: "جمعه",

  // dashboard — getting started (new hall owner)
  start_title: "د کار پیل — تر لومړي بکنګ پورې",
  start_step_hall: "خپل تالار ثبت کړئ: نوم، ښار، ظرفیت او کرایه",
  start_step_menu: "مینو او فی کس بیه اضافه کړئ، او عکس کېږدئ",
  start_step_publish:
    "د خپرولو لپاره یې ولیږئ — له تایید وروسته، پیرودونکي ستاسو تالار په اپ کې ویني او بک یې کوي",
  start_pending: "ستاسو تالار بیاکتنې ته لیږل شوی — له تایید وروسته د پیرودونکو په اپ کې ښکاري.",
  start_go_halls: "تالارونو ته تګ",

  // finance — account types
  acct_type_asset: "شتمني",
  acct_type_liability: "پور",
  acct_type_equity: "پانګه",
  acct_type_income: "عاید",
  acct_type_expense: "لګښت",

  // confirmations + accessible labels (UI/UX pass)
  common_confirm: "تایید",
  cp_show_password: "پټنوم ښودل",
  cp_hide_password: "پټنوم پټول",
  confirm_ledger_note: "په دفترونو کې ثبت نه سمیږي؛ سمون یوازې د معکوس سند له لارې کیږي.",
  confirm_verify_title: "دا رسید دې په دفترونو کې ثبت شي؟",
  confirm_verify_body: "رسید {no} په {amount} اندازه تاییدیږي او په دفترونو کې ثبتیږي.",
  confirm_payroll_title: "معاش دې تادیه او ثبت شي؟",
  confirm_payroll_body: "د {period} معاش لیست چې ټولټال یې {total} دی، تادیه شوی ثبتیږي او دفترونو ته ځي.",
  confirm_paid_title: "دا تسویه دې تادیه شوې ثبت شي؟",
  confirm_paid_body: "د {org} سازمان ته د {amount} تسویه تادیه شوې نښه کیږي. یوازې هغه وخت یې تایید کړئ چې پیسې په رښتیا لیږدول شوې وي.",
  confirm_settle_title: "تسویه دې پلي شي؟",
  confirm_settle_body: "د ټولو تایید شویو آنلاین تادیاتو لپاره چې لا تسویه شوي نه دي، تسویه جوړیږي.",
  confirm_reject_hall_title: "دا تالار دې رد شي؟",
  confirm_reject_hall_body: "«{name}» نه خپریږي.",
  confirm_reject_review_title: "دا نظر دې رد شي؟",
  confirm_reject_review_body: "دا نظر نه خپریږي.",
};
